import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, inr, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast, Toaster } from "sonner";
import { FloppyDisk, Cube, TreeStructure, Package, ArrowsClockwise } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const emptyParts = { rm: "", wip: "", fg: "", notes: "" };
const nowMonth = () => new Date().toISOString().slice(0, 7);

export default function Stock() {
    const { user } = useAuth();
    const canEdit = ["founder", "accountant"].includes(user?.role);

    const [month, setMonth] = useState(nowMonth());
    const [startMonth, setStartMonth] = useState("2026-08");
    const [data, setData] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    const [opening, setOpening] = useState(emptyParts);
    const [closing, setClosing] = useState(emptyParts);
    const [savingOpen, setSavingOpen] = useState(false);
    const [savingClose, setSavingClose] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, h, sm] = await Promise.all([
                api.get(`/stock/${month}`),
                api.get(`/stock`),
                api.get(`/settings/pnl-start-month`),
            ]);
            setData(s.data);
            setHistory(h.data);
            setStartMonth(sm.data.month);
            // Fill form state from what's in the DB (or blank if not yet entered)
            const op = s.data.opening;
            setOpening(op ? { rm: String(op.rm), wip: String(op.wip), fg: String(op.fg), notes: op.notes || "" } : emptyParts);
            const cl = s.data.closing;
            setClosing(cl ? { rm: String(cl.rm), wip: String(cl.wip), fg: String(cl.fg), notes: cl.notes || "" } : emptyParts);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [month]);
    useEffect(() => { load(); }, [load]);

    const partsTotal = (p) => (Number(p.rm) || 0) + (Number(p.wip) || 0) + (Number(p.fg) || 0);

    const save = async (kind) => {
        const p = kind === "opening" ? opening : closing;
        const setSaving = kind === "opening" ? setSavingOpen : setSavingClose;
        const rm = Number(p.rm);
        const wip = Number(p.wip);
        const fg = Number(p.fg);
        if ([rm, wip, fg].some((v) => Number.isNaN(v) || v < 0)) {
            return toast.error("RM, WIP, FG must be numbers ≥ 0");
        }
        setSaving(true);
        try {
            await api.put(`/stock/${month}/${kind}`, { rm, wip, fg, notes: p.notes || null });
            toast.success(`${kind === "opening" ? "Opening" : "Closing"} stock saved`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const beforeStart = month < startMonth;

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Stock (periodic)"
                sub={
                    <>
                        Manual opening & closing stock for the month, split into <b>RM</b>, <b>WIP</b>, and <b>FG</b>. This
                        drives Cost of Goods Sold on the Monthly P&amp;L. Live inventory / BOM validation is coming later.
                    </>
                }
                right={
                    <div className="flex items-center gap-2">
                        <Label className="text-xs text-stone-500">Month</Label>
                        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                               className="w-40" data-testid="stock-month-picker" />
                        <Button variant="outline" onClick={load} disabled={loading} data-testid="stock-refresh">
                            <ArrowsClockwise size={14} className="mr-1" /> Refresh
                        </Button>
                    </div>
                }
            />

            <div className="p-8 space-y-6">
                {beforeStart ? (
                    <div className="p-4 border rounded-md bg-amber-50 text-amber-900 text-sm" data-testid="stock-before-start">
                        Monthly P&amp;L only starts from <b>{startMonth}</b>. Change the P&amp;L start month in Settings if you need earlier data.
                    </div>
                ) : null}

                <div className="grid gap-6 md:grid-cols-2">
                    <StockCard
                        title="Opening stock"
                        subtitle={
                            data?.inherited_opening && !data?.opening
                                ? <>Inherited from closing of <b>{data.inherited_opening.inherited_from}</b> (₹ {inr(data.inherited_opening.total)}). Save to override.</>
                                : "Enter the value on hand at the first day of the month."
                        }
                        parts={opening}
                        setParts={setOpening}
                        canEdit={canEdit}
                        onSave={() => save("opening")}
                        saving={savingOpen}
                        entered={data?.opening}
                        testidPrefix="stock-opening"
                        accent="#0d6efd"
                    />
                    <StockCard
                        title="Closing stock"
                        subtitle="Enter this at month-end. Once saved, the month's P&L is finalised."
                        parts={closing}
                        setParts={setClosing}
                        canEdit={canEdit}
                        onSave={() => save("closing")}
                        saving={savingClose}
                        entered={data?.closing}
                        testidPrefix="stock-closing"
                        accent="#059669"
                    />
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                    <SummaryTile label="Opening total" value={partsTotal(opening)} testid="stock-opening-total" />
                    <SummaryTile
                        label="Closing total"
                        value={partsTotal(closing)}
                        testid="stock-closing-total"
                    />
                    <SummaryTile
                        label="Δ Change"
                        value={partsTotal(closing) - partsTotal(opening)}
                        signed
                        testid="stock-change"
                    />
                </div>

                <div className="text-[11px] text-stone-500 px-1">
                    Monthly P&amp;L finalises once closing stock is entered (periodic-inventory basis). Live P&amp;L is coming later.
                </div>

                <HistoryTable rows={history} />
            </div>
        </>
    );
}

function StockCard({ title, subtitle, parts, setParts, canEdit, onSave, saving, entered, testidPrefix, accent }) {
    const total = (Number(parts.rm) || 0) + (Number(parts.wip) || 0) + (Number(parts.fg) || 0);
    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid={`${testidPrefix}-card`}>
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)", borderTop: `3px solid ${accent}` }}>
                <div className="font-serif text-lg" style={{ color: accent }}>{title}</div>
                <div className="text-xs text-stone-500 mt-0.5">{subtitle}</div>
                {entered ? (
                    <div className="text-[11px] text-stone-500 mt-1">
                        Last saved on <b>{formatDate(entered.updated_at)}</b>
                    </div>
                ) : null}
            </div>
            <div className="p-5 space-y-4">
                <PartRow icon={<Cube size={14} weight="duotone" />} label="RM (Raw Material)"
                         value={parts.rm} onChange={(v) => setParts({ ...parts, rm: v })}
                         disabled={!canEdit || saving} testid={`${testidPrefix}-rm`} />
                <PartRow icon={<TreeStructure size={14} weight="duotone" />} label="WIP (Work-in-Progress)"
                         value={parts.wip} onChange={(v) => setParts({ ...parts, wip: v })}
                         disabled={!canEdit || saving} testid={`${testidPrefix}-wip`} />
                <PartRow icon={<Package size={14} weight="duotone" />} label="FG (Finished Goods)"
                         value={parts.fg} onChange={(v) => setParts({ ...parts, fg: v })}
                         disabled={!canEdit || saving} testid={`${testidPrefix}-fg`} />

                <div className="border-t pt-3 flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                    <div className="text-xs uppercase tracking-widest text-stone-500">Total</div>
                    <div className="se-num text-2xl font-medium" data-testid={`${testidPrefix}-total-display`}>
                        ₹ {inr(total)}
                    </div>
                </div>

                <div>
                    <Label>Notes</Label>
                    <Textarea rows={2} value={parts.notes || ""} onChange={(e) => setParts({ ...parts, notes: e.target.value })}
                              disabled={!canEdit || saving} placeholder="Optional context" data-testid={`${testidPrefix}-notes`} />
                </div>

                <div className="flex justify-end">
                    <Button onClick={onSave} disabled={!canEdit || saving} style={{ background: accent, color: "#fff" }} data-testid={`${testidPrefix}-save`}>
                        <FloppyDisk size={14} className="mr-2" /> {saving ? "Saving…" : `Save ${title.toLowerCase()}`}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function PartRow({ icon, label, value, onChange, disabled, testid }) {
    return (
        <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 min-w-[180px] text-sm text-stone-700">
                {icon} {label}
            </div>
            <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">₹</span>
                <Input type="number" step="0.01" value={value}
                       onChange={(e) => onChange(e.target.value)}
                       disabled={disabled} className="pl-7 se-num text-right"
                       placeholder="0.00" data-testid={testid} />
            </div>
        </div>
    );
}

function SummaryTile({ label, value, signed, testid }) {
    const color = signed ? (value < 0 ? "#dc2626" : value > 0 ? "#059669" : "#0f172a") : "#0f172a";
    const sign = signed && value > 0 ? "+" : "";
    return (
        <div className="border rounded-md px-5 py-4 bg-white" style={{ borderColor: "var(--se-border)" }} data-testid={testid}>
            <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className="se-num text-2xl mt-1 font-medium" style={{ color }}>
                {sign}₹ {inr(Math.abs(value))}
            </div>
        </div>
    );
}

function HistoryTable({ rows }) {
    // Group by month for a compact view
    const byMonth = {};
    rows.forEach((r) => {
        byMonth[r.month] = byMonth[r.month] || {};
        byMonth[r.month][r.kind] = r;
    });
    const months = Object.keys(byMonth).sort().reverse();
    if (months.length === 0) return null;
    return (
        <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid="stock-history">
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg">Prior months</div>
                <div className="text-xs text-stone-500">Closing of month N is treated as opening of month N+1 by default.</div>
            </div>
            <table className="se-table text-sm">
                <thead>
                    <tr>
                        <th>Month</th>
                        <th className="text-right">Opening total</th>
                        <th className="text-right">Closing total</th>
                        <th className="text-right">Δ</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {months.map((m) => {
                        const op = byMonth[m].opening;
                        const cl = byMonth[m].closing;
                        const delta = (cl?.total || 0) - (op?.total || 0);
                        return (
                            <tr key={m} data-testid={`stock-history-row-${m}`}>
                                <td className="font-medium">{m}</td>
                                <td className="se-num text-right">{op ? `₹ ${inr(op.total)}` : "—"}</td>
                                <td className="se-num text-right">{cl ? `₹ ${inr(cl.total)}` : <span className="text-amber-600">not entered</span>}</td>
                                <td className="se-num text-right" style={{ color: delta < 0 ? "#dc2626" : delta > 0 ? "#059669" : "#78716c" }}>
                                    {cl && op ? `${delta > 0 ? "+" : ""}₹ ${inr(Math.abs(delta))}` : "—"}
                                </td>
                                <td className="text-right text-[11px] text-stone-500">
                                    RM ₹ {inr(op?.rm ?? cl?.rm ?? 0)} · WIP ₹ {inr(op?.wip ?? cl?.wip ?? 0)} · FG ₹ {inr(op?.fg ?? cl?.fg ?? 0)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
